import { yupResolver } from '@hookform/resolvers/yup';
import {
  OrderAdjustmentModel,
  OrderAdjustmentMutationInput,
} from 'api-hooks/order-adjustment';
import { TransactionalStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState } from 'components/elements';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import { TitleContainer } from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { OrderAdjustmentFormSchema } from './form-type';
import InformationField from './information-field';
import PurchaseDetailCard from './purchase-detail-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  orderAdjustment?: OrderAdjustmentModel;
  onSubmit: (
    input: OrderAdjustmentMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<OrderAdjustmentModel | undefined>;
  renderError?: React.ReactNode;
}

export default function OrderAdjustmentForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { orderAdjustment } = props;

  const defaultValues = useGetDefaultValues(orderAdjustment);

  const methods = useForm({
    resolver: yupResolver(OrderAdjustmentFormSchema()),
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e?.message });
      }
    },
    [methods, props],
  );

  const isActive = orderAdjustment?.status === TransactionalStatusEnum.Active;

  const permission = useGetCrudPermission('order-adjustments');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'order-adjustments',
    orderAdjustment,
  );
  const { resolverButton } = useResolveApproval(
    'order-adjustments',
    orderAdjustment,
  );
  const { requestApprovalButton } = useApprovalAction(
    'order-adjustments',
    orderAdjustment,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'order-adjustments',
    orderAdjustment,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (orderAdjustment) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    orderAdjustment,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);

  const orderId = methods.getValues('purchaseId');
  React.useEffect(() => {
    if (!orderId || orderId === '') {
      methods.reset();
      methods.setValue('purchase.number', '');
      methods.setValue('purchase.transactionAt', null);
    }
  }, [methods, orderId]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!orderAdjustment}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        orderAdjustment &&
        `${t('common:order_adjustment')} - ${orderAdjustment.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={orderAdjustment}
            onDelete={onVoid}
            noDelete={editable || !voidPermission}
            noEdit={!isActive || !permission.update}
            title={
              orderAdjustment ? (
                <TitleContainer>
                  <Text variant="HeadingXSmall">
                    {`${t('common:order_adjustment')} - ${
                      orderAdjustment.number
                    }`}
                  </Text>
                  <TransactionPillStatus value={orderAdjustment.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', { extra: t('common:order_adjustment') })
              )
            }
            isDeleteLoading={voidMutate.isLoading}
            deleteText={t('common:void')}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      {props.renderError}
      <div>
        <Separator gap={16} />
        <InformationField />
        <PurchaseDetailCard />
        <Separator gap={16} />
      </div>
    </Form>
  );
}
